import { useEffect, useState, FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { testimonials } from '@/mocks/data';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  useEffect(() => {
    document.title = '关于我们 - 雏田同人馆';
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      return;
    }
    formData.delete('phone_alt');

    setFormStatus('loading');
    try {
      const response = await fetch('https://readdy.ai/api/form/d9rb3vfnahb7382ovk5g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      const serverMsg = parsed?.meta?.message || parsed?.message || '';
      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
        setFormStatus('error');
        setFormMessage('提交失败，请稍后重试。');
      } else {
        setFormStatus('error');
        setFormMessage(serverMsg || '提交失败，请稍后重试。');
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后重试。');
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 雏田同人馆',
    description: '雏田同人馆是专注火影忍者雏田角色同人创作的内容聚合平台，致力于为火影忍者粉丝提供最全面的雏田本子资源导览与推荐。',
    url: 'https://hinata-doujin.readdy.ai/about',
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '雏田同人馆',
    url: 'https://hinata-doujin.readdy.ai',
    description: '专注火影忍者雏田角色同人创作的内容聚合平台',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@hinata-doujin.readdy.ai',
      availableLanguage: ['Chinese'],
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 pt-16">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '关于我们' },
            ]}
          />

          <section className="w-full px-4 md:px-6 lg:px-10 py-10 md:py-16 bg-background-50">
            <div className="max-w-4xl mx-auto">
              <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground-900 mb-6">
                关于我们
              </h1>
              <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-8">
                雏田同人馆是专注于火影忍者日向雏田角色同人创作的内容聚合平台。我们致力于为全球火影忍者粉丝打造一个高质量、有温度的雏田同人作品推荐与导览空间。
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
                <div className="bg-background-100 rounded-lg p-6 text-center">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-100 mx-auto mb-4">
                    <i className="ri-heart-line text-2xl text-primary-600"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-foreground-900 mb-2">热爱驱动</h3>
                  <p className="text-sm text-foreground-500">
                    由一群热爱火影忍者的粉丝发起，源于对雏田角色的喜爱和对同人文化的热情。
                  </p>
                </div>
                <div className="bg-background-100 rounded-lg p-6 text-center">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-accent-100 mx-auto mb-4">
                    <i className="ri-star-line text-2xl text-accent-600"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-foreground-900 mb-2">精选品质</h3>
                  <p className="text-sm text-foreground-500">
                    编辑团队严格筛选，只推荐高质量的同人作品，确保每一篇都值得阅读与收藏。
                  </p>
                </div>
                <div className="bg-background-100 rounded-lg p-6 text-center">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-secondary-100 mx-auto mb-4">
                    <i className="ri-group-line text-2xl text-secondary-600"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-foreground-900 mb-2">连接社区</h3>
                  <p className="text-sm text-foreground-500">
                    搭建创作者与粉丝之间的桥梁，促进同人文化的交流与传播。
                  </p>
                </div>
              </div>

              <div className="mb-14">
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-6">
                  我们的故事
                </h2>
                <div className="space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                  <p>
                    雏田同人馆诞生于一个简单的想法：为喜欢日向雏田这个角色的火影粉丝们提供一个可以轻松发现和浏览优质同人作品的地方。
                  </p>
                  <p>
                    作为火影忍者中备受喜爱的角色，雏田以其温柔、坚强和不屈不挠的精神感染了无数观众。在官方剧情之外，大量优秀的同人创作者用自己的方式诠释着这个角色，创作出了丰富多彩的同人漫画、插画和小说作品。然而，这些优秀的作品散落在互联网的各个角落，粉丝们往往需要花费大量时间才能找到自己喜爱的内容。
                  </p>
                  <p>
                    于是，雏田同人馆应运而生。我们聚合了来自不同平台的雏田同人作品，按照类型进行系统化的分类和整理，让每一位粉丝都能在这里轻松发现心仪的作品。从最初的几十部作品到如今收录数百部优质同人创作，我们始终保持着对品质的坚持和对同人文化的尊重。
                  </p>
                  <p>
                    我们相信，同人创作是粉丝文化中最具创造力和生命力的部分。它不仅是对原作热爱的表达，更是粉丝们发挥想象力、展现才华的舞台。雏田同人馆愿意成为这片创作热土的守护者和传播者，让更多优秀的同人作品被看见、被欣赏。
                  </p>
                </div>
              </div>

              <div className="mb-14">
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-6">
                  用户口碑
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {testimonials.map((t, index) => (
                    <div key={index} className="bg-background-100 rounded-lg p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <img
                          src={t.avatar}
                          alt={t.name}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                        <div>
                          <p className="text-sm font-semibold text-foreground-900">{t.name}</p>
                          <p className="text-xs text-foreground-500">{t.role}</p>
                        </div>
                      </div>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        &ldquo;{t.content}&rdquo;
                      </p>
                      <div className="flex items-center gap-0.5 mt-3">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <div key={star} className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-star-fill text-sm text-primary-500"></i>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-background-100 rounded-lg p-8 md:p-10">
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-6">
                  联系我们
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                      如果您有任何建议、合作意向，或者想要投稿您的雏田同人作品，欢迎通过以下方式与我们取得联系。我们的编辑团队会在24小时内回复您的消息。
                    </p>
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                          <i className="ri-mail-line text-primary-600"></i>
                        </div>
                        <div>
                          <p className="text-xs text-foreground-400">电子邮箱</p>
                          <p className="text-sm text-foreground-800 font-medium">contact@hinata-doujin.readdy.ai</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                          <i className="ri-wechat-line text-accent-600"></i>
                        </div>
                        <div>
                          <p className="text-xs text-foreground-400">微信公众号</p>
                          <p className="text-sm text-foreground-800 font-medium">雏田同人馆</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 flex-shrink-0">
                          <i className="ri-bilibili-line text-secondary-600"></i>
                        </div>
                        <div>
                          <p className="text-xs text-foreground-400">Bilibili</p>
                          <p className="text-sm text-foreground-800 font-medium">雏田同人馆官方</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <form className="space-y-4" onSubmit={handleSubmit} data-readdy-form="">
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="honeypot-field"
                    />
                    <div>
                      <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1">姓名</label>
                      <input
                        type="text"
                        id="about-name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1">邮箱</label>
                      <input
                        type="email"
                        id="about-email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1">留言</label>
                      <textarea
                        id="about-message"
                        name="message"
                        rows={4}
                        maxLength={500}
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                        placeholder="请输入您的留言（限500字）"
                      ></textarea>
                    </div>
                    {formStatus !== 'idle' && (
                      <div className={`text-sm px-4 py-2.5 rounded-lg ${
                        formStatus === 'success' ? 'bg-accent-100 text-accent-800' :
                        formStatus === 'error' ? 'bg-red-50 text-red-700' :
                        'bg-secondary-100 text-secondary-700'
                      }`}>
                        {formStatus === 'loading' ? '正在发送...' : formMessage}
                      </div>
                    )}
                    <button
                      type="submit"
                      disabled={formStatus === 'loading'}
                      className="w-full px-6 py-3 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
                    >
                      {formStatus === 'loading' ? '发送中...' : '发送消息'}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}